import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../services/Firebase';
import { BRANDING } from '../../config/branding';
import { deleteMyAccount } from '../../api/user';
import ProfileManagement from '../ProfileManagement';
import SubscriptionManagement from '../SubscriptionManagement';
import {
  Header,
  Main,
  Nav,
  NavButton,
  Page,
  Section,
  Subtitle,
  Title,
} from './styles';

const Settings = () => {
  const location = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(location.search);
  const tab = query.get('tab') || 'subscription-management';
  const packageSectionRef = useRef(null);
  const profileSectionRef = useRef(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const scrollToSection = ref => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleLogout = async () => {
    try { await signOut(auth); } catch (_) {}
    history.push('/');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteMyAccount();
      await signOut(auth).catch(() => {});
      history.push('/');
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <Page>
      <Main>
        <Helmet>
          <title>{`${BRANDING.appName} | Settings`}</title>
          <meta name="description" content="Manage your account, billing, and subscription access." />
        </Helmet>

        <Header>
          <Title>Settings</Title>
          <Subtitle>Manage your profile, monthly plan, and credits from one place.</Subtitle>
        </Header>

        <Nav>
          <NavButton type="button" onClick={() => scrollToSection(packageSectionRef)}>
            Billing & Credits
          </NavButton>
          <NavButton type="button" onClick={() => scrollToSection(profileSectionRef)}>
            Profile & Account
          </NavButton>
        </Nav>

        <Section id="subscription-management" ref={packageSectionRef}>
          {tab === 'subscription-management' ? <SubscriptionManagement /> : <SubscriptionManagement />}
        </Section>

        <Section id="profile-management" ref={profileSectionRef}>
          {tab === 'profile' ? <ProfileManagement /> : <ProfileManagement />}
        </Section>

        <div style={{ marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <button
            onClick={() => { setShowDeleteModal(true); setDeleteConfirm(''); setDeleteError(''); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.45rem',
              background: 'transparent', border: '1px solid rgba(239,68,68,0.35)',
              color: '#f87171', borderRadius: '999px', padding: '0.55rem 1.25rem',
              fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer',
            }}
          >
            <i className="fas fa-trash-alt" /> Delete Account
          </button>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.45rem',
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)',
              color: '#f87171', borderRadius: '999px', padding: '0.55rem 1.25rem',
              fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer',
            }}
          >
            <i className="fas fa-sign-out-alt" /> Log out
          </button>
        </div>

        {showDeleteModal && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
          }}>
            <div style={{
              background: '#0f1117', border: '1px solid rgba(239,68,68,0.35)',
              borderRadius: 20, padding: '2rem', maxWidth: 440, width: '100%',
              boxShadow: '0 24px 60px rgba(0,0,0,0.7)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
                <i className="fas fa-exclamation-triangle" style={{ color: '#f87171', fontSize: '1.3rem' }} />
                <h3 style={{ margin: 0, color: '#f1f5f9', fontFamily: 'Poppins, sans-serif', fontSize: '1.1rem' }}>Delete Account</h3>
              </div>
              <p style={{ color: '#cbd5e1', fontSize: '0.92rem', lineHeight: 1.6, marginBottom: '0.5rem' }}>
                This will permanently delete your account and remove all data from our systems:
              </p>
              <ul style={{ color: '#94a3b8', fontSize: '0.88rem', lineHeight: 1.7, marginBottom: '1.25rem', paddingLeft: '1.2rem' }}>
                <li>All active Stripe subscriptions will be cancelled</li>
                <li>Your Stripe customer record will be deleted</li>
                <li>Your profile will be removed from our database</li>
                <li>Your login credentials will be deleted</li>
              </ul>
              <p style={{ color: '#f87171', fontSize: '0.88rem', fontWeight: 600, marginBottom: '0.6rem' }}>
                This action cannot be undone. Type <strong>DELETE</strong> to confirm.
              </p>
              <input
                type="text"
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                placeholder="Type DELETE to confirm"
                style={{
                  width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 10, color: '#f1f5f9', padding: '0.65rem 0.8rem',
                  fontSize: '0.92rem', marginBottom: '0.9rem', boxSizing: 'border-box',
                  outline: deleteConfirm === 'DELETE' ? '1px solid #f87171' : 'none',
                }}
                autoFocus
                autoComplete="off"
              />
              {deleteError && (
                <p style={{ color: '#f87171', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{deleteError}</p>
              )}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={() => { setShowDeleteModal(false); setDeleteConfirm(''); setDeleteError(''); }}
                  disabled={deleting}
                  style={{
                    flex: 1, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
                    color: '#e2e8f0', borderRadius: 999, padding: '0.65rem 1rem',
                    fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirm !== 'DELETE' || deleting}
                  style={{
                    flex: 1, background: deleteConfirm === 'DELETE' ? '#dc2626' : 'rgba(220,38,38,0.2)',
                    border: '1px solid rgba(220,38,38,0.5)', color: '#ffffff',
                    borderRadius: 999, padding: '0.65rem 1rem',
                    fontWeight: 700, fontSize: '0.9rem',
                    cursor: deleteConfirm === 'DELETE' && !deleting ? 'pointer' : 'not-allowed',
                    opacity: deleteConfirm === 'DELETE' && !deleting ? 1 : 0.55,
                    transition: 'all 0.2s',
                  }}
                >
                  {deleting ? 'Deleting...' : 'Delete My Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </Main>
    </Page>
  );
};

export default Settings;
